import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  TableOptions,
  ColumnDef,
  Column
} from '@tanstack/react-table'
import { ComponentType, useMemo } from 'react'
import { faChevronUp, faChevronDown } from '../../icons/light'
import { Button } from '../Button/Button'
import { Icon } from '../Icon/Icon'

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    gridWidth?: string | null
    gridMinWidth?: string
    gridMaxWidth?: string
    filter?: ComponentType<{ column: Column<TData, TValue> }>
  }
}

export const createExpandoColumn = <Data,>(): TableOptions<Data>['columns'][number] => ({
  id: 'core-expand',
  header: ({ table }) => (
    <Button
      variant='plain'
      onClick={() => table.toggleAllRowsExpanded()}
    >
      <Icon
        className='!w-4 text-gray-600'
        icon={table.getIsAllRowsExpanded() ? faChevronUp : faChevronDown}
      />
    </Button>
  ),
  enableSorting: false,
  cell: ({ row }) => (
    <Button
      variant='plain'
      onClick={() => row.toggleExpanded()}
    >
      <Icon
        className='!w-4 text-gray-600'
        icon={row.getIsExpanded() ? faChevronUp : faChevronDown}
      />
    </Button>
  )
})
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useNomTable = <D extends object = {}>(
  options:
  & Omit<TableOptions<D>, 'getCoreRowModel' | 'getPaginationRowModel' | 'getSortedRowModel' | 'getExpandedRowModel' | 'autoResetPageIndex'>
) => {
  const table = useReactTable(
    {
      defaultColumn: useMemo<Partial<ColumnDef<D, unknown>>>(
        () => ({
          cell: context => (context.getValue() === 0 ? 0 : context.getValue() as boolean || '-')
        }),
        []
      ),
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getExpandedRowModel: getExpandedRowModel(),

      // Default these to `true`, but allow callers to overwrite them.
      //
      // Not doing this might cause runtime issues. For example: users of the hook might to add filters to
      // columns and forget setting up `manualFiltering` to `true`. In this case, react-table
      // will assume the filter value is a string and will try to run `.toLowercase()` on whichever
      // value it gets. Doing this when the value's type is something else will crash the page.
      //
      // We also don't want to force users to set these to true in case that's what they actually want,
      // but they'll have to set that flag to false themselves.
      manualExpanding: true,
      manualFiltering: true,
      manualGrouping: true,
      manualPagination: true,

      ...options,

      // Reset the `pageIndex` when the `globalFilter` changes.
      // The alternative to this is to set `autoResetPageIndex` to `true`,
      // but that will cause the index to be reset whenever anything in the
      // table state changes, including `data`.
      // That causes undesired behaviour when used together with `react-query`:
      // the filters change, the query returns the data and `pageIndex` is reset,
      // causing the table to only show the first page forever.
      onGlobalFilterChange: globalFilter => {
        table.resetPageIndex()
        table.setState(oldState => ({
          ...oldState,
          globalFilter: (typeof globalFilter === 'function')
            ? globalFilter(oldState.globalFilter)
            : globalFilter
        }))
      },

      // Reset the `pageIndex` when the `columnFilters` change.
      // The alternative to this is to set `autoResetPageIndex` to `true`,
      // but that will cause the index to be reset whenever anything in the
      // table state changes, including `data`.
      // That causes undesired behaviour when used together with `react-query`:
      // the filters change, the query returns the data and `pageIndex` is reset,
      // causing the table to only show the first page forever.
      onColumnFiltersChange: columnFilters => {
        table.resetPageIndex()
        table.setState(oldState => ({
          ...oldState,
          columnFilters: (typeof columnFilters === 'function')
            ? columnFilters(oldState.columnFilters)
            : columnFilters
        }))
      },

      columns: useMemo(
        () => options.columns.map(
          column => ({
            ...column,
            meta: {
              ...column.meta,
              gridWidth: column.meta?.gridWidth ?? null,
              gridMinWidth: column.meta?.gridMinWidth ?? 'min-content',
              gridMaxWidth: column.meta?.gridMaxWidth ?? '1fr'
            }
          })
        ),
        [options.columns]
      )
    }
  )

  return table
}
